import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Check, Clock, Loader2, ChevronDown, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useFM } from '@/context/FMContext';
import { axiosInstance } from '@/App';

/**
 * Format an ISO timestamp to a readable time string in IST.
 */
function formatTime(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString('en-IN', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  } catch {
    return isoString;
  }
}

/**
 * Format an ISO timestamp to a date + time label.
 */
function formatDateTime(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const isToday = d.toDateString() === today.toDateString();
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const time = d.toLocaleTimeString('en-IN', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });

    if (isToday) return `Today, ${time}`;
    if (isYesterday) return `Yesterday, ${time}`;

    return d.toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric',
      timeZone: 'Asia/Kolkata',
    }) + ', ' + time;
  } catch {
    return isoString;
  }
}

/**
 * Check if an ISO timestamp is from today (IST).
 */
function isToday(isoString) {
  if (!isoString) return false;
  try {
    const d = new Date(isoString);
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const dIST = new Date(d.getTime() + istOffset);
    const nowIST = new Date(now.getTime() + istOffset);
    return dIST.toISOString().slice(0, 10) === nowIST.toISOString().slice(0, 10);
  } catch {
    return false;
  }
}

/**
 * GymCheckInCard - renders a single gym's check-in card.
 */
function GymCheckInCard({ gym, todayCheckin, lastCheckin, onCheckIn }) {
  const [checking, setChecking] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const alreadyCheckedIn = !!todayCheckin;
  const gymName = gym.name || gym.gym_name || gym.gym_id;

  const handleCheckIn = async () => {
    setChecking(true);
    try {
      const body = {
        gym_id: gym.gym_id || gym._id,
        type: 'virtual',
      };
      if (notes.trim()) {
        body.notes = notes.trim();
      }
      await onCheckIn(body);
      toast.success(`Checked in at ${gymName}`);
      setNotes('');
      setShowNotes(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="rounded-xl border p-5 bg-white shadow-sm">
      {/* Gym Header */}
      <div className="mb-3">
        <h3 className="text-base font-semibold text-slate-800">{gymName}</h3>
        <span className="text-xs text-slate-500">
          Status: {gym.status || 'Active'}
        </span>
      </div>

      {/* Check-in Action */}
      {alreadyCheckedIn ? (
        <div className="flex items-center gap-2 py-3 px-4 rounded-lg bg-green-50 border border-green-200">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-sm font-medium text-green-700">
            Checked in today at {formatTime(todayCheckin.timestamp)}
          </span>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Optional notes input - collapsible */}
          {showNotes ? (
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes (optional)..."
              rows={2}
              className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          ) : (
            <button
              onClick={() => setShowNotes(true)}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Add Notes
            </button>
          )}

          {/* Big green Check In button */}
          <button
            onClick={handleCheckIn}
            disabled={checking}
            className="w-full h-12 rounded-lg bg-green-500 hover:bg-green-600 text-white font-semibold text-base flex items-center justify-center gap-2 active:scale-[0.97] transition-all disabled:opacity-60"
          >
            {checking ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Checking in...
              </>
            ) : (
              <>
                <MapPin className="w-5 h-5" />
                Check In
              </>
            )}
          </button>
        </div>
      )}

      {/* Last check-in info */}
      {lastCheckin && !alreadyCheckedIn && (
        <p className="mt-3 text-xs text-slate-400 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          Last check-in: {formatDateTime(lastCheckin.timestamp)}
        </p>
      )}
      {todayCheckin?.notes && (
        <p className="mt-2 text-xs text-slate-500 italic">
          Notes: {todayCheckin.notes}
        </p>
      )}
    </div>
  );
}

/**
 * FMCheckIn - Virtual gym check-in page.
 * Shows ALL assigned gyms with one-tap check-in, check-in history, and monthly summary.
 */
export default function FMCheckIn() {
  const { gyms, loading: gymsLoading } = useFM();
  const [checkins, setCheckins] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingCheckins, setLoadingCheckins] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 20;

  // Gym name lookup
  const gymNameMap = {};
  gyms.forEach((g) => {
    const id = g.gym_id || g._id;
    gymNameMap[id] = g.name || g.gym_name || id;
  });

  // Fetch today's check-ins for all gyms
  const fetchTodayCheckins = useCallback(async () => {
    if (gyms.length === 0) return;
    setLoadingCheckins(true);
    try {
      const now = new Date();
      const istDate = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const todayStr = istDate.toISOString().slice(0, 10);
      const res = await axiosInstance.get('/fm/checkins', {
        params: { date_from: todayStr, date_to: todayStr },
      });
      setCheckins(res.data.checkins || []);
    } catch {
      // Non-critical
    } finally {
      setLoadingCheckins(false);
    }
  }, [gyms]);

  // Fetch check-in history (current month default)
  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await axiosInstance.get('/fm/checkins');
      setHistory(res.data.checkins || []);
    } catch {
      // Non-critical
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchTodayCheckins();
    fetchHistory();
  }, [fetchTodayCheckins, fetchHistory]);

  // Handle check-in for a gym
  const handleCheckIn = async (body) => {
    const res = await axiosInstance.post('/fm/checkins', body);
    // Update today's checkins locally
    setCheckins((prev) => [res.data, ...prev]);
    // Also add to history
    setHistory((prev) => [res.data, ...prev]);
  };

  // Pull-to-refresh
  const handleRefresh = async () => {
    await Promise.all([fetchTodayCheckins(), fetchHistory()]);
  };

  // Today's check-in map by gym_id
  const todayCheckinMap = {};
  checkins.forEach((c) => {
    if (isToday(c.timestamp)) {
      // Keep the first (most recent) for each gym
      if (!todayCheckinMap[c.gym_id]) {
        todayCheckinMap[c.gym_id] = c;
      }
    }
  });

  // Last check-in per gym (excluding today)
  const lastCheckinMap = {};
  history.forEach((c) => {
    if (!isToday(c.timestamp) && !lastCheckinMap[c.gym_id]) {
      lastCheckinMap[c.gym_id] = c;
    }
  });

  // Monthly summary stats
  const thisMonthCheckins = history.length;
  const uniqueGymsChecked = new Set(history.map((c) => c.gym_id)).size;

  // Mini calendar: which days had check-ins
  const checkinDays = new Set();
  history.forEach((c) => {
    if (c.timestamp) {
      try {
        const d = new Date(c.timestamp);
        checkinDays.add(d.toISOString().slice(8, 10));
      } catch {
        // skip
      }
    }
  });

  // Paginated history
  const visibleHistory = history.slice(0, historyPage * HISTORY_PAGE_SIZE);
  const hasMore = visibleHistory.length < history.length;

  const isLoading = gymsLoading || loadingCheckins;

  return (
    <PullToRefresh onRefresh={handleRefresh} pullingContent="" refreshingContent={<Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />}>
      <div className="space-y-5 pb-20">
        {/* Page Title */}
        <div>
          <h2 className="text-lg font-bold text-slate-800">Virtual Check-in</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            One-tap check-in for your assigned gyms
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border p-5 bg-white shadow-sm animate-pulse">
                <div className="w-40 h-5 bg-slate-200 rounded mb-2" />
                <div className="w-20 h-3 bg-slate-100 rounded mb-4" />
                <div className="w-full h-12 bg-slate-100 rounded-lg" />
              </div>
            ))}
          </div>
        )}

        {/* Gym Cards for Check-in */}
        {!isLoading && gyms.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <MapPin className="w-10 h-10 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">No assigned gyms found</p>
          </div>
        )}

        {!isLoading && gyms.length > 0 && (
          <div className="space-y-4">
            {gyms.map((gym) => {
              const gymId = gym.gym_id || gym._id;
              return (
                <GymCheckInCard
                  key={gymId}
                  gym={gym}
                  todayCheckin={todayCheckinMap[gymId]}
                  lastCheckin={lastCheckinMap[gymId]}
                  onCheckIn={handleCheckIn}
                />
              );
            })}
          </div>
        )}

        {/* Monthly Summary Stats */}
        {!loadingHistory && history.length > 0 && (
          <div className="rounded-lg bg-slate-50 border p-3 flex items-center justify-between text-xs text-slate-600">
            <span>
              This month: <span className="font-semibold text-slate-800">{thisMonthCheckins}</span> check-in{thisMonthCheckins !== 1 ? 's' : ''}
            </span>
            <span className="text-slate-300">|</span>
            <span>
              Across <span className="font-semibold text-slate-800">{uniqueGymsChecked}</span> gym{uniqueGymsChecked !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Mini Calendar - Days with check-ins */}
        {!loadingHistory && checkinDays.size > 0 && (
          <div className="rounded-lg border p-3 bg-white">
            <p className="text-xs font-medium text-slate-600 mb-2">Days with check-ins</p>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: 31 }, (_, i) => {
                const day = String(i + 1).padStart(2, '0');
                const hasCheckin = checkinDays.has(day);
                return (
                  <span
                    key={day}
                    className={`w-7 h-7 flex items-center justify-center rounded text-xs font-medium ${
                      hasCheckin
                        ? 'bg-green-500 text-white'
                        : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {i + 1}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Check-in History */}
        {!loadingHistory && history.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Recent Check-ins</h3>
            <div className="space-y-2">
              {visibleHistory.map((c, idx) => (
                <div
                  key={c.checkin_id || idx}
                  className="flex items-start justify-between p-3 rounded-lg border bg-white"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {gymNameMap[c.gym_id] || c.gym_id}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {formatDateTime(c.timestamp)}
                    </p>
                    {c.notes && (
                      <p className="text-xs text-slate-400 mt-1 italic truncate">{c.notes}</p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ml-2 ${
                    c.type === 'physical'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    {c.type || 'virtual'}
                  </span>
                </div>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <button
                onClick={() => setHistoryPage((p) => p + 1)}
                className="w-full mt-3 py-2.5 rounded-lg border text-sm font-medium text-slate-600 hover:bg-slate-50 active:scale-[0.98] transition-all flex items-center justify-center gap-1.5"
              >
                <ChevronDown className="w-4 h-4" />
                Load More
              </button>
            )}
          </div>
        )}
      </div>
    </PullToRefresh>
  );
}
